import { useState } from 'react';
import axios from 'axios';
import { Form, FormGroup, FormLabel, FormControl, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import { motion, AnimatePresence } from "framer-motion";
import MainLogo from "../../../../images/main-logo.svg";
import LoginImage from "../../../../images/otp-image.png";
import AuthLeftSection from "../AuthLeftSection/AuthLeftSection.tsx";
import AuthBackground from "../AuthBackground/AuthBackground.tsx";
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from "../../../../icons";
import apiConfig from '../../config/apiConfig.js';
import { forgotPasswordSchema } from '../utils/validationSchema.js';
import { sanitizers } from '../utils/inputSanitizers.js';
import { API_ENDPOINTS, STORAGE_KEYS } from '../utils/constants.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./Forgot.scss"

function Forgot() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            email: ''
        },
        validationSchema: forgotPasswordSchema,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setLoading(true);

                const response = await axios.post(
                    `${apiConfig.apiBaseURL}${API_ENDPOINTS.FORGOT_PASSWORD}`,
                    {
                        email: values.email.trim(),
                        isResend: false
                    },
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (response.data.status === "Success") {
                    localStorage.setItem(STORAGE_KEYS.FORGOT_PASSWORD_EMAIL, values.email.trim());
                    
                    toast.success(response.data.message || 'OTP sent to your email address successfully!');
                    
                    // Navigate with flow state (preferred method)
                    navigate('/otp', {
                        state: {
                            flow: "forgot",
                            email: values.email.trim()
                        }
                    });
                } else {
                    // Handle failed status from backend
                    toast.error(response.data.message || 'Failed to send OTP. Please try again.');
                }
            } catch (error) {
                console.error('Forgot password failed', error);
                
                // Handle different error scenarios
                if (error.response?.status === 404) {
                    toast.error('No account found with this email address.');
                } else {
                    toast.error(error.response?.data?.message || 'Failed to send OTP. Please try again.');
                }
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        }
    });

    // Prevent space key from being pressed
    const handleKeyDown = (e: any) => {
        if (e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
        }
    };

    // Animation variants for smooth, calm transitions
    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          duration: 0.8,
          ease: "easeOut",
          staggerChildren: 0.2
        }
      }
    };

    const slideInLeft = {
      hidden: { opacity: 0, x: -60, scale: 0.95 },
      visible: {
        opacity: 1,
        x: 0,
        scale: 1,
        transition: {
          duration: 1,
          ease: [0.25, 0.46, 0.45, 0.94]
        }
      }
    };

    const slideInRight = {
      hidden: { opacity: 0, x: 60, scale: 0.95 },
      visible: {
        opacity: 1,
        x: 0,
        scale: 1,
        transition: {
          duration: 1,
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: 0.3
        }
      }
    };

    const fadeInUp = {
      hidden: { opacity: 0, y: 30 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.8,
          ease: "easeOut"
        }
      }
    };

    const floatingAnimation = {
      animate: {
        y: [0, -8, 0],
        transition: {
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }
    };

    const buttonHover = {
      scale: 1.02,
      transition: { duration: 0.2, ease: "easeInOut" }
    };

    const inputFocus = {
      scale: 1.01,
      boxShadow: "0 8px 30px rgba(57, 87, 160, 0.12)",
      transition: { duration: 0.3, ease: "easeOut" }
    };

    return (
        <>
          {/* Full-screen background animations */}
          <AuthBackground />

          <motion.section
            className='login-screen'
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 15px', position: 'relative', zIndex: 1 }}
          >
            <div style={{ maxWidth: '1600px', width: '100%' }}>
            <div className='row' style={{ width: '100%', margin: '0' }}>
                {/* Left Panel - Enhanced Image and Content */}
                <AuthLeftSection
                  image={LoginImage}
                  imageAlt="Forgot Password"
                  title="Giving Presence to Future Moments"
                  description="We're more than a service—it's a movement to redefine how love endures."
                  slideInLeft={slideInLeft}
                  fadeInUp={fadeInUp}
                  floatingAnimation={floatingAnimation}
                />

                {/* Right Panel - Forgot Password Form */}
                <div className='col-md-6' style={{ display: 'flex' }}>
                  {/* OUTER WRAPPER - Has background and padding */}
                  <motion.div
                    className='login-screen-right-wrapper'
                    variants={slideInRight}
                    initial="hidden"
                    animate="visible"
                    style={{
                      height: 'calc(100vh - 60px)',
                      width: '100%',
                      background: '#F2F3F7',
                      borderRadius: '24px'
                    }}
                  >
                    {/* INNER SCROLLABLE CONTAINER */}
                    <div
                      className='login-screen-right'
                      style={{
                        height: '100%',
                        overflowY: 'auto',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div className='login-screen-right-in' style={{ background: 'transparent' }}>
                        
                        <motion.div
                          className='middle-block'
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.8,
                            ease: "easeOut",
                            delay: 0.6
                          }}
                        >
                            <motion.figure
                              className='main-logo'
                              initial={{ scale: 0.5, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{
                                duration: 0.8,
                                ease: "easeOut",
                                delay: 0.8
                              }}
                              whileHover={{ scale: 1.05 }}
                            >
                                <img src={MainLogo} alt='' />
                            </motion.figure>

                            <motion.h3
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.6,
                                ease: "easeOut",
                                delay: 1
                              }}
                              style={{ marginBottom: '1rem', color: '#2c3e50' }}
                            >
                                Forgot Password
                            </motion.h3>

                            <motion.p
                              className='otp-text'
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.6,
                                ease: "easeOut",
                                delay: 1.1
                              }}
                              style={{ 
                                marginBottom: '2rem', 
                                color: '#677289', 
                                lineHeight: '1.5',
                                textAlign: 'center'
                              }}
                            >
                                Enter your email, and we'll send you a One-Time Password (OTP) to reset your password.
                            </motion.p>
                            
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 1.3, duration: 0.6 }}
                            >
                              <Form onSubmit={formik.handleSubmit} className='py-0'>
                                  <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 1.4, duration: 0.6 }}
                                  >
                                    <FormGroup className="form-group">
                                        <FormLabel style={{ fontWeight: '600', color: '#2c3e50' }}>Email Address</FormLabel>
                                        <motion.div whileFocus={inputFocus}>
                                          <FormControl 
                                              autoFocus
                                              type="email" 
                                              placeholder="Enter Email address" 
                                              value={formik.values.email}
                                              onChange={(e) => {
                                                  const sanitized = sanitizers.email(e.target.value);
                                                  formik.setFieldValue("email", sanitized);
                                              }}
                                              onBlur={formik.handleBlur}
                                              className={`form-control p-3`}
                                              autoComplete="email"
                                              onKeyDown={handleKeyDown}
                                              disabled={loading}
                                              style={{
                                                border: '2px solid #e0e6ed',
                                                borderRadius: '12px',
                                                transition: 'all 0.3s ease'
                                              }}
                                          />
                                        </motion.div>
                                       
                                        <AnimatePresence>
                                          {formik.touched.email && formik.errors.email && (
                                              <motion.small
                                                  initial={{ opacity: 0, y: -10 }}
                                                  animate={{ opacity: 1, y: 0 }}
                                                  exit={{ opacity: 0, y: -10 }}
                                                  style={{
                                                      color: '#dc3545',
                                                      display: 'block',
                                                      marginTop: '8px',
                                                      fontSize: '0.875rem'
                                                  }}
                                              >
                                                  {formik.errors.email}
                                              </motion.small>
                                          )}
                                        </AnimatePresence>
                                    </FormGroup>
                                  </motion.div>

                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.6, duration: 0.6 }}
                                    whileHover={buttonHover}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    <Button
                                        className='main-bttn'
                                        type="submit"
                                        disabled={loading}
                                        style={{
                                          width: '100%',
                                          padding: '14px 20px',
                                          background: loading
                                            ? 'linear-gradient(135deg, #bbb, #999)'
                                            : '#D7A360',
                                          border: 'none',
                                          borderRadius: '12px',
                                          fontSize: '16px',
                                          fontWeight: '600',
                                          boxShadow: '0 8px 20px rgba(215, 163, 96, 0.3)',
                                          transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <AnimatePresence mode="wait">
                                          {loading ? (
                                            <motion.span
                                              key="loading"
                                              initial={{ opacity: 0 }}
                                              animate={{ opacity: 1 }}
                                              exit={{ opacity: 0 }}
                                            >
                                              <motion.span
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                style={{ display: 'inline-block', marginRight: '8px' }}
                                              >
                                                ⟳
                                              </motion.span>
                                              Sending...
                                            </motion.span>
                                          ) : (
                                            <motion.span
                                              key="submit"
                                              initial={{ opacity: 0 }}
                                              animate={{ opacity: 1 }}
                                              exit={{ opacity: 0 }}
                                            >
                                              Send OTP
                                            </motion.span>
                                          )}
                                        </AnimatePresence>
                                    </Button>
                                    <motion.div
                                        className='back-to-login'
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 1.8, duration: 0.6 }}
                                    >
                                        <motion.div whileHover={{ scale: 1.02 }}>
                                          <Link
                                              to="/"
                                              style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '8px',
                                              color: '#3957A0',
                                              fontSize: '18px',
                                              fontWeight: '600',
                                              textDecoration: 'none'
                                              }}
                                          >
                                          <FiArrowLeft size={20} />
                                          Back to Login
                                          </Link>
                                        </motion.div>
                                    </motion.div>
                                  </motion.div>
                              </Form>
                            </motion.div>
                        </motion.div>
                    </div>
                    </div>
                  </motion.div>
                </div>
            </div>
            </div>
          </motion.section>
        </>
    );
}

export default Forgot;